/**
 * GreedyMesh — collapses a sculpted voxel volume's exposed faces into the minimal set of
 * coplanar same-colour quads (plus the surface-voxel list the death shatter spawns from).
 */

import { volIndex } from './Sculpt.ts';
import type { VoxelVolume } from './Sculpt.ts';

export interface MeshData {
  positions: Float32Array;   // 3/vertex, world units, origin re-centred
  normals: Float32Array;     // 3/vertex
  colors: Float32Array;      // 3/vertex, LINEAR
  spineT: Float32Array;      // 1/vertex, 0 at nose, 1 at tail tip
  /** 1/vertex animation group (1 body, 2 mandible, 3 rostrum); null when no groups supplied */
  jawGroup: Float32Array | null;
  quadCount: number;
  triangleCount: number;
  /** world-space AABB size after scaling */
  size: [number, number, number];
}

export interface SurfaceVoxels {
  /** 3/voxel, world-space offsets from the same recentred origin */
  offsets: Float32Array;
  colors: Float32Array;      // 3/voxel, LINEAR
  count: number;
  voxelSize: number;
}

/**
 * Per-sweep-axis (u, v) basis, chosen CYCLICALLY — u = (d+1)%3, v = (d+2)%3 — so that
 * e_u x e_v === +e_d for all three axes. The classic bug here is picking u/v in ascending
 * index order (X:1,2  Y:0,2  Z:0,1), which makes the Y sweep left-handed and silently
 * inverts the winding of two of the six directions. With a uniformly right-handed basis
 * the vertex order depends only on `dir`, never on the axis:
 *
 *   corners in (u,v):  0 = (u0,v0)   1 = (u1,v0)   2 = (u1,v1)   3 = (u0,v1)
 *
 *   axis d  dir  u  v  plane      outward N  vertex order        CCW seen from
 *   ------  ---  -  -  ---------  ---------  ------------------  -------------
 *   0 (X)   +1   Y  Z  x = c + 1  +X         0,1,2 | 0,2,3       +X
 *   0 (X)   -1   Y  Z  x = c      -X         0,3,2 | 0,2,1       -X
 *   1 (Y)   +1   Z  X  y = c + 1  +Y         0,1,2 | 0,2,3       +Y
 *   1 (Y)   -1   Z  X  y = c      -Y         0,3,2 | 0,2,1       -Y
 *   2 (Z)   +1   X  Y  z = c + 1  +Z         0,1,2 | 0,2,3       +Z
 *   2 (Z)   -1   X  Y  z = c      -Z         0,3,2 | 0,2,1       -Z
 *
 * Proof for the +dir row: (p1-p0) x (p2-p0) = (du*e_u) x (du*e_u + dv*e_v)
 * = du*dv * (e_u x e_v) = du*dv * e_d, and du,dv > 0 — so the right-hand normal is the
 * outward normal, i.e. the loop is CCW from outside. Reversing gives the -dir row.
 */
const AXIS_U = [1, 2, 0] as const;
const AXIS_V = [2, 0, 1] as const;
const ORDER_POS = new Uint8Array([0, 1, 2, 0, 2, 3]);
const ORDER_NEG = new Uint8Array([0, 3, 2, 0, 2, 1]);

/** Packed quad record: d, dir, plane, i0, j0, width(u), height(v), slotPlusOne. */
const QUAD_STRIDE = 8;

function emptyResult(voxelSize: number): { mesh: MeshData; surface: SurfaceVoxels } {
  // Fresh buffers per field: the worker transfers each ArrayBuffer, and transferring one
  // shared empty buffer six times throws.
  return {
    mesh: {
      positions: new Float32Array(0),
      normals: new Float32Array(0),
      colors: new Float32Array(0),
      spineT: new Float32Array(0),
      jawGroup: null,
      quadCount: 0,
      triangleCount: 0,
      size: [0, 0, 0],
    },
    surface: {
      offsets: new Float32Array(0),
      colors: new Float32Array(0),
      count: 0,
      voxelSize,
    },
  };
}

export function greedyMesh(
  vol: VoxelVolume,
  palette: Float32Array,     // SLOT_COUNT * 3 linear RGB
  voxelSize: number,
  maxSurfaceVoxels: number,
  /**
   * Optional per-voxel animation group (1..3), parallel to vol.data. When present it is
   * folded into the merge key, so a quad can never span two groups — the jaw parting
   * surface would otherwise be welded shut by the merger and tear open on the first bite.
   * Emitted per vertex as `jawGroup`.
   */
  groups?: Uint8Array,
): { mesh: MeshData; surface: SurfaceVoxels } {
  if (vol.solidCount <= 0) return emptyResult(voxelSize);

  const dims = vol.dims;
  const data = vol.data;
  const w = dims[0];
  const h = dims[1];
  const l = dims[2];
  const wh = w * h;
  /** matches volIndex: index = x + w*(y + h*z) */
  const stride = [1, w, wh] as const;

  const minB = vol.min;
  const maxB = vol.max;

  // Recentring: origin at the centre of the SOLID AABB on all three axes. The lattice
  // spans [min, max+1] in each axis, so the centre sits on the half-integer midpoint.
  const cx = (minB[0] + maxB[0] + 1) * 0.5;
  const cy = (minB[1] + maxB[1] + 1) * 0.5;
  const cz = (minB[2] + maxB[2] + 1) * 0.5;

  // spineT is read off the GRID z before recentring. Nose is high z -> 0, tail z=0 -> 1,
  // which is what makes the shader's pow(spineT, 1.6) whip the tail and not the head.
  const zTop = maxB[2] + 1;
  const invZSpan = 1 / (zTop - minB[2]);

  // Highest legal palette slot; a sculpt that writes an unknown slot clamps instead of
  // reading past the table and poisoning the colour buffer with NaN.
  const maxSlot = Math.max(0, ((palette.length / 3) | 0) - 1);

  // One scratch mask sized to the largest of the three slice planes, reused by all six
  // sweeps. Slot values are 1..SLOT_COUNT so a Uint8Array is both correct and cache-tight.
  // Uint16 because the key is (slot+1)<<2 | group when groups are supplied, which overflows
  // a byte past 63 slots. Without groups it holds the raw slot+1 exactly as before.
  const mask = new Uint16Array(Math.max(h * l, l * w, w * h));
  const KEY = groups !== undefined;

  // Quads accumulate as packed integer records and expand to vertices once, at the end.
  // Growing 8 ints/quad is far cheaper than growing 60 floats/quad, and the final vertex
  // buffers come out exactly sized with no trailing slack.
  let recCap = Math.max(256, Math.min(vol.solidCount, 4096));
  let rec = new Int32Array(recCap * QUAD_STRIDE);
  let quadCount = 0;

  const sweep = (d: number, dir: number): void => {
    const u = AXIS_U[d];
    const v = AXIS_V[d];
    const du = dims[u];
    const dv = dims[v];
    const dd = dims[d];
    const su = stride[u];
    const sv = stride[v];
    const sd = stride[d];
    const nOff = dir * sd;

    for (let c = 0; c < dd; c++) {
      const nc = c + dir;
      const nOob = nc < 0 || nc >= dd;   // hoisted: the whole slice shares this verdict
      const base = c * sd;

      let anyFace = 0;
      for (let j = 0; j < dv; j++) {
        const row = base + j * sv;
        const mrow = j * du;
        for (let i = 0; i < du; i++) {
          const idx = row + i * su;
          const val = data[idx];
          const exposed = val !== 0 && (nOob || data[idx + nOff] === 0);
          const m = exposed ? (KEY ? (val << 2) | (groups as Uint8Array)[idx] : val) : 0;
          mask[mrow + i] = m;
          anyFace |= m;
        }
      }
      if (anyFace === 0) continue;   // interior slices are the common case; skip the merge

      const plane = dir > 0 ? c + 1 : c;
      for (let j = 0; j < dv; j++) {
        const mrow = j * du;
        let i = 0;
        while (i < du) {
          const slot = mask[mrow + i];
          if (slot === 0) { i++; continue; }

          let qw = 1;
          while (i + qw < du && mask[mrow + i + qw] === slot) qw++;

          let qh = 1;
          grow: while (j + qh < dv) {
            const r = (j + qh) * du + i;
            for (let k = 0; k < qw; k++) if (mask[r + k] !== slot) break grow;
            qh++;
          }

          for (let b = 0; b < qh; b++) {
            const r = (j + b) * du + i;
            mask.fill(0, r, r + qw);
          }

          if (quadCount === recCap) {
            recCap *= 2;
            const grown = new Int32Array(recCap * QUAD_STRIDE);
            grown.set(rec);
            rec = grown;
          }
          const o = quadCount * QUAD_STRIDE;
          rec[o] = d;
          rec[o + 1] = dir;
          rec[o + 2] = plane;
          rec[o + 3] = i;
          rec[o + 4] = j;
          rec[o + 5] = qw;
          rec[o + 6] = qh;
          rec[o + 7] = slot;
          quadCount++;

          i += qw;
        }
      }
    }
  };

  for (let d = 0; d < 3; d++) {
    sweep(d, 1);
    sweep(d, -1);
  }

  // --- expand records to non-indexed vertices ------------------------------------------
  const vertCount = quadCount * 6;
  const positions = new Float32Array(vertCount * 3);
  const normals = new Float32Array(vertCount * 3);
  const colors = new Float32Array(vertCount * 3);
  const spineT = new Float32Array(vertCount);
  const jawGroup = KEY ? new Float32Array(vertCount) : null;

  const lat = new Int32Array(12);   // 4 lattice corners, xyz each
  let vp = 0;
  let vt = 0;

  for (let q = 0; q < quadCount; q++) {
    const o = q * QUAD_STRIDE;
    const d = rec[o];
    const dir = rec[o + 1];
    const plane = rec[o + 2];
    const i0 = rec[o + 3];
    const j0 = rec[o + 4];
    const i1 = i0 + rec[o + 5];
    const j1 = j0 + rec[o + 6];
    const u = AXIS_U[d];
    const v = AXIS_V[d];

    lat[d] = plane;     lat[u] = i0;  lat[v] = j0;
    lat[3 + d] = plane; lat[3 + u] = i1; lat[3 + v] = j0;
    lat[6 + d] = plane; lat[6 + u] = i1; lat[6 + v] = j1;
    lat[9 + d] = plane; lat[9 + u] = i0; lat[9 + v] = j1;

    const key = rec[o + 7];
    const slot = (KEY ? key >> 2 : key) - 1;
    const grp = KEY ? key & 3 : 0;
    const ps = (slot < maxSlot ? slot : maxSlot) * 3;
    const cr = palette[ps];
    const cg = palette[ps + 1];
    const cb = palette[ps + 2];

    // Flat face normal: exactly +/-1 on one axis, never averaged — the hard voxel facets
    // are the art direction, not an artefact.
    const nx = d === 0 ? dir : 0;
    const ny = d === 1 ? dir : 0;
    const nz = d === 2 ? dir : 0;

    const order = dir > 0 ? ORDER_POS : ORDER_NEG;
    for (let k = 0; k < 6; k++) {
      const b = order[k] * 3;
      const lz = lat[b + 2];

      positions[vp] = (lat[b] - cx) * voxelSize;
      positions[vp + 1] = (lat[b + 1] - cy) * voxelSize;
      positions[vp + 2] = (lz - cz) * voxelSize;
      normals[vp] = nx;
      normals[vp + 1] = ny;
      normals[vp + 2] = nz;
      colors[vp] = cr;
      colors[vp + 1] = cg;
      colors[vp + 2] = cb;

      // Per VERTEX, not per quad: on X- and Y-facing quads the z and z+1 corners get
      // different spineT, and that gradient is exactly what lets one long merged quad
      // deform smoothly along the body instead of shearing as a rigid slab.
      const t = (zTop - lz) * invZSpan;
      spineT[vt] = t < 0 ? 0 : t > 1 ? 1 : t;
      if (jawGroup !== null) jawGroup[vt] = grp;

      vp += 3;
      vt++;
    }
  }

  // --- surface voxels (death shatter source) -------------------------------------------
  // Every solid voxel with at least one exposed face. Collected as linear indices first so
  // the decimation is a single pass over a compact list rather than a second grid scan.
  let surfCap = Math.max(1024, Math.min(vol.solidCount, 16384));
  let surf = new Int32Array(surfCap);
  let surfN = 0;

  for (let z = 0; z < l; z++) {
    for (let y = 0; y < h; y++) {
      const rowBase = volIndex(dims, 0, y, z);
      for (let x = 0; x < w; x++) {
        const idx = rowBase + x;
        if (data[idx] === 0) continue;
        const exposed =
          x === 0 || data[idx - 1] === 0 ||
          x === w - 1 || data[idx + 1] === 0 ||
          y === 0 || data[idx - w] === 0 ||
          y === h - 1 || data[idx + w] === 0 ||
          z === 0 || data[idx - wh] === 0 ||
          z === l - 1 || data[idx + wh] === 0;
        if (!exposed) continue;
        if (surfN === surfCap) {
          surfCap *= 2;
          const grown = new Int32Array(surfCap);
          grown.set(surf);
          surf = grown;
        }
        surf[surfN++] = idx;
      }
    }
  }

  // Bresenham-style fractional stride: emits exactly min(surfN, cap) samples spread
  // uniformly across the list, integer-only so it is bit-identical everywhere.
  const cap = maxSurfaceVoxels > 0 ? maxSurfaceVoxels | 0 : 0;
  const keep = surfN < cap ? surfN : cap;
  const offsets = new Float32Array(keep * 3);
  const surfColors = new Float32Array(keep * 3);
  let err = 0;
  let so = 0;

  for (let k = 0; k < surfN; k++) {
    err += cap;
    if (err < surfN) continue;
    err -= surfN;

    const idx = surf[k];
    const z = (idx / wh) | 0;
    const rem = idx - z * wh;
    const y = (rem / w) | 0;
    const x = rem - y * w;

    // Voxel CENTRE, from the same recentred origin as the mesh, so a shattered chunk
    // starts exactly where its face was.
    offsets[so] = (x + 0.5 - cx) * voxelSize;
    offsets[so + 1] = (y + 0.5 - cy) * voxelSize;
    offsets[so + 2] = (z + 0.5 - cz) * voxelSize;

    const slot = data[idx] - 1;
    const ps = (slot < maxSlot ? slot : maxSlot) * 3;
    surfColors[so] = palette[ps];
    surfColors[so + 1] = palette[ps + 1];
    surfColors[so + 2] = palette[ps + 2];

    so += 3;
  }

  return {
    mesh: {
      positions,
      normals,
      colors,
      spineT,
      jawGroup,
      quadCount,
      triangleCount: quadCount * 2,
      size: [
        (maxB[0] - minB[0] + 1) * voxelSize,
        (maxB[1] - minB[1] + 1) * voxelSize,
        (maxB[2] - minB[2] + 1) * voxelSize,
      ],
    },
    surface: {
      offsets,
      colors: surfColors,
      count: so / 3,
      voxelSize,
    },
  };
}
